import escapedString from 'escape-string-regexp';
import { useCallback, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';

export function useClientSearch<T>(
  collection: T[] | null | undefined,
  callback: (item: T, matcher: RegExp) => RegExpMatchArray | null,
  { delay = 0 } = {}
) {
  const [value, set] = useState('');

  const reset = useCallback(() => set(''), []);

  const [debouncedValue] = useDebounce(
    value,
    // remove debounce delay for tests
    process.env.NODE_ENV === 'test' ? 0 : delay
  );

  const result = useMemo(() => {
    if (!collection) return [];
    if (!debouncedValue) return collection;

    const search = new RegExp(escapedString(value.toString().toLowerCase()));
    return collection.filter((item) => callback(item, search));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collection, debouncedValue]);

  return {
    result,
    value,
    set,
    reset,
    /** Only relevant if delay is set. */
    debouncedValue,
    selectProps: {
      mode: 'client-search' as const,
      searchValue: value,
      setSearchValue: set,
      minSearchValueLength: 0,
      searchResult: result,
    },
  };
}
